import { useSuspenseQuery } from "@apollo/client";
import type { AccountCardAccount, ButtonVariant, TxtVariant } from "@repo/ui";
import {
  AccountCard,
  Button,
  Card,
  CardContent,
  PrimaryFullScreenSpinner,
  Txt,
} from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

// Not linked anywhere — just a scratch page for eyeballing the palette.
export const Route = createFileRoute("/__auth/design")({
  head: () => opsHead("Design"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const ACCOUNT_UI_SAMPLE_IDS = {
  fxAsset: "asset_01kryqnch0en8bthttdj2tkwz4", // FX wallet, has a logo
  bankAccountAsset: "asset_01kz8aftcqfaq98r6jf4bv8xay", // linked external bank account
  fixedIncomeFundAsset: "asset_01kzaxskwhe4atazjgkcv33hv6", // fixed income fund
  maturedTermDepositAsset: "asset_01krft0ctteh5vwxhargd11y8p", // "Matured" badge
  maturingSoonTermDepositAsset: "asset_01kt5max5be04sjwbccw34j7bn", // "Maturing soon" badge
  defaultBeneficiaryAccount: "ba_01j7f503jsee29kb8fafaybjaw", // default beneficiary, verified
  disabledBeneficiaryAccount: "ba_01kb18nc6te4bsgw141e48fsc9", // deactivated beneficiary
} as const;

function useAccountUiSampleAccounts(): AccountCardAccount[] {
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminDesignAccountUiSamples(
        $fxAssetId: ID!
        $bankAccountAssetId: ID!
        $fixedIncomeFundAssetId: ID!
        $maturedTermDepositAssetId: ID!
        $maturingSoonTermDepositAssetId: ID!
        $defaultBeneficiaryAccountId: ID!
        $disabledBeneficiaryAccountId: ID!
      ) {
        fxAsset: asset(id: $fxAssetId) {
          ...AssetCardFields
        }
        bankAccountAsset: asset(id: $bankAccountAssetId) {
          ...AssetCardFields
        }
        fixedIncomeFundAsset: asset(id: $fixedIncomeFundAssetId) {
          ...AssetCardFields
        }
        maturedTermDepositAsset: asset(id: $maturedTermDepositAssetId) {
          ...AssetCardFields
        }
        maturingSoonTermDepositAsset: asset(
          id: $maturingSoonTermDepositAssetId
        ) {
          ...AssetCardFields
        }
        defaultBeneficiaryAccount: beneficiaryAccount(
          id: $defaultBeneficiaryAccountId
        ) {
          ...BeneficiaryAssetCardFields
        }
        disabledBeneficiaryAccount: beneficiaryAccount(
          id: $disabledBeneficiaryAccountId
        ) {
          ...BeneficiaryAssetCardFields
        }
      }
    `),
    {
      variables: {
        fxAssetId: ACCOUNT_UI_SAMPLE_IDS.fxAsset,
        bankAccountAssetId: ACCOUNT_UI_SAMPLE_IDS.bankAccountAsset,
        fixedIncomeFundAssetId: ACCOUNT_UI_SAMPLE_IDS.fixedIncomeFundAsset,
        maturedTermDepositAssetId:
          ACCOUNT_UI_SAMPLE_IDS.maturedTermDepositAsset,
        maturingSoonTermDepositAssetId:
          ACCOUNT_UI_SAMPLE_IDS.maturingSoonTermDepositAsset,
        defaultBeneficiaryAccountId:
          ACCOUNT_UI_SAMPLE_IDS.defaultBeneficiaryAccount,
        disabledBeneficiaryAccountId:
          ACCOUNT_UI_SAMPLE_IDS.disabledBeneficiaryAccount,
      },
    },
  );

  return [
    data.fxAsset,
    data.bankAccountAsset,
    data.fixedIncomeFundAsset,
    data.maturedTermDepositAsset,
    data.maturingSoonTermDepositAsset,
    data.defaultBeneficiaryAccount,
    data.disabledBeneficiaryAccount,
  ].filter((account): account is AccountCardAccount => account != null);
}

function AccountUiVariantSection({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Txt variant="caption">{label}</Txt>
      <div className="flex flex-col gap-3 rounded-md border p-4">
        {children}
      </div>
    </div>
  );
}

type Swatch = { label: string; className: string };

const SEMANTIC_SWATCHES: Swatch[] = [
  { label: "bg-black", className: "bg-black" },
  { label: "bg-white", className: "bg-white" },

  { label: "bg-brand", className: "bg-brand" },
  { label: "bg-brand-highlight", className: "bg-brand-highlight" },

  { label: "bg-primary", className: "bg-primary" },
  {
    label: "bg-primary-interactive-hover",
    className: "bg-primary-interactive-hover",
  },
  { label: "bg-primary-inverse", className: "bg-primary-inverse" },
  {
    label: "bg-primary-inverse-interactive-hover",
    className: "bg-primary-inverse-interactive-hover",
  },

  { label: "bg-secondary", className: "bg-secondary" },
  { label: "bg-secondary-interactive", className: "bg-secondary-interactive" },
  {
    label: "bg-secondary-interactive-hover",
    className: "bg-secondary-interactive-hover",
  },

  { label: "bg-canvas", className: "bg-canvas" },

  { label: "bg-success-muted", className: "bg-success-muted" },
  { label: "bg-success", className: "bg-success" },
  { label: "bg-success-solid", className: "bg-success-solid" },

  { label: "bg-info-muted", className: "bg-info-muted" },
  { label: "bg-info", className: "bg-info" },
  { label: "bg-info-solid", className: "bg-info-solid" },

  { label: "bg-warning-muted", className: "bg-warning-muted" },
  { label: "bg-warning", className: "bg-warning" },
  { label: "bg-warning-solid", className: "bg-warning-solid" },

  { label: "bg-danger-muted", className: "bg-danger-muted" },
  { label: "bg-danger", className: "bg-danger" },
  { label: "bg-danger-solid", className: "bg-danger-solid" },

  { label: "bg-star", className: "bg-star" },

  { label: "bg-neutral-muted", className: "bg-neutral-muted" },
  { label: "bg-neutral", className: "bg-neutral" },
  { label: "bg-neutral-solid", className: "bg-neutral-solid" },
];

const HUE_FAMILIES: { name: string; swatches: Swatch[] }[] = [
  {
    name: "steel",
    swatches: [
      { label: "bg-steel-100", className: "bg-steel-100" },
      { label: "bg-steel-200", className: "bg-steel-200" },
      { label: "bg-steel-300", className: "bg-steel-300" },
      { label: "bg-steel-400", className: "bg-steel-400" },
      { label: "bg-steel-500", className: "bg-steel-500" },
      { label: "bg-steel-600", className: "bg-steel-600" },
      { label: "bg-steel-700", className: "bg-steel-700" },
      { label: "bg-steel-800", className: "bg-steel-800" },
    ],
  },
  {
    name: "petrol",
    swatches: [
      { label: "bg-petrol-100", className: "bg-petrol-100" },
      { label: "bg-petrol-200", className: "bg-petrol-200" },
      { label: "bg-petrol-300", className: "bg-petrol-300" },
      { label: "bg-petrol-400", className: "bg-petrol-400" },
      { label: "bg-petrol-500", className: "bg-petrol-500" },
      { label: "bg-petrol-600", className: "bg-petrol-600" },
      { label: "bg-petrol-700", className: "bg-petrol-700" },
      { label: "bg-petrol-800", className: "bg-petrol-800" },
    ],
  },
  {
    name: "cobalt",
    swatches: [
      { label: "bg-cobalt-100", className: "bg-cobalt-100" },
      { label: "bg-cobalt-200", className: "bg-cobalt-200" },
      { label: "bg-cobalt-300", className: "bg-cobalt-300" },
      { label: "bg-cobalt-400", className: "bg-cobalt-400" },
      { label: "bg-cobalt-500", className: "bg-cobalt-500" },
      { label: "bg-cobalt-600", className: "bg-cobalt-600" },
      { label: "bg-cobalt-700", className: "bg-cobalt-700" },
      { label: "bg-cobalt-800", className: "bg-cobalt-800" },
    ],
  },
  {
    name: "indigo",
    swatches: [
      { label: "bg-indigo-100", className: "bg-indigo-100" },
      { label: "bg-indigo-200", className: "bg-indigo-200" },
      { label: "bg-indigo-300", className: "bg-indigo-300" },
      { label: "bg-indigo-400", className: "bg-indigo-400" },
      { label: "bg-indigo-500", className: "bg-indigo-500" },
      { label: "bg-indigo-600", className: "bg-indigo-600" },
      { label: "bg-indigo-700", className: "bg-indigo-700" },
      { label: "bg-indigo-800", className: "bg-indigo-800" },
    ],
  },
  {
    name: "violet",
    swatches: [
      { label: "bg-violet-100", className: "bg-violet-100" },
      { label: "bg-violet-200", className: "bg-violet-200" },
      { label: "bg-violet-300", className: "bg-violet-300" },
      { label: "bg-violet-400", className: "bg-violet-400" },
      { label: "bg-violet-500", className: "bg-violet-500" },
      { label: "bg-violet-600", className: "bg-violet-600" },
      { label: "bg-violet-700", className: "bg-violet-700" },
      { label: "bg-violet-800", className: "bg-violet-800" },
    ],
  },
];

function SwatchGrid({ swatches }: { swatches: Swatch[] }) {
  return (
    <div className="grid grid-cols-8 gap-4">
      {swatches.map((swatch) => (
        <div
          key={swatch.className}
          className="flex flex-col items-center gap-1"
        >
          <div className={`size-16 rounded-md border ${swatch.className}`} />
          <Txt variant="caption" truncate>
            {swatch.label}
          </Txt>
        </div>
      ))}
    </div>
  );
}

const BUTTON_VARIANTS: ButtonVariant[] = [
  "primary",
  "secondary",
  "outline",
  "ghost",
  "destructive",
  "accent",
];

const TXT_VARIANTS: TxtVariant[] = [
  "hero-title",
  "page-title",
  "section-title",
  "card-title",
  "field-label",
  "detail-label",
  "column-label",
  "body",
  "value",
  "metric",
  "page-description",
  "description",
  "caption",
  "name",
  "mono",
];

function View() {
  const accounts = useAccountUiSampleAccounts();

  return (
    <AdminPageContainer testId="design-palette">
      <div className="flex flex-col gap-4">
        <Card className="bg-white">
          <CardContent className="flex flex-col gap-4 p-6">
            <Txt variant="section-title">Bg palette</Txt>
            <SwatchGrid swatches={SEMANTIC_SWATCHES} />
            {HUE_FAMILIES.map((family) => (
              <SwatchGrid key={family.name} swatches={family.swatches} />
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="flex flex-col gap-4 p-6">
            <Txt variant="section-title">Buttons</Txt>
            <div className="flex flex-wrap items-center gap-4">
              {BUTTON_VARIANTS.map((variant) => (
                <Button key={variant} variant={variant}>
                  {variant.charAt(0).toUpperCase() + variant.slice(1)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="flex flex-col gap-4 p-6">
            <Txt variant="section-title">Txt</Txt>
            <div className="flex flex-col gap-3">
              {TXT_VARIANTS.map((variant) => (
                <div key={variant} className="flex items-baseline gap-4">
                  <Txt variant="caption" nowrap>
                    {variant}
                  </Txt>
                  <Txt variant={variant}>The quick brown fox</Txt>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="flex flex-col gap-6 p-6">
            <Txt variant="section-title">Account UI</Txt>

            <AccountUiVariantSection label="DEFAULT">
              {accounts.map((account) => (
                <AccountCard
                  key={account.id}
                  account={account}
                  subText={["BALANCE", "ACCOUNT_TYPE", "PROVENANCE"]}
                  rightAdornment={[
                    "MASKED_IDENTIFIER",
                    "VERIFIED_BENEFICIARY",
                    "PENDING",
                    "DEFAULT_BENEFICIARY",
                    "DEACTIVATED",
                  ]}
                />
              ))}
            </AccountUiVariantSection>

            <AccountUiVariantSection label="MINIMAL">
              {accounts.map((account) => (
                <AccountCard
                  key={account.id}
                  account={account}
                  variant="MINIMAL"
                />
              ))}
            </AccountUiVariantSection>

            <AccountUiVariantSection label="COMPACT_CARD">
              <div className="flex flex-wrap gap-3">
                {accounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    variant="COMPACT_CARD"
                  />
                ))}
              </div>
            </AccountUiVariantSection>

            <AccountUiVariantSection label="INLINE">
              <div className="flex flex-wrap gap-3">
                {accounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    variant="INLINE"
                  />
                ))}
              </div>
            </AccountUiVariantSection>

            <AccountUiVariantSection label="INLINE_TEXT">
              <div className="flex flex-wrap items-center gap-4">
                {accounts.map((account) => (
                  <span
                    key={account.id}
                    className="inline-flex items-center gap-2"
                  >
                    <AccountCard
                      account={account}
                      variant="INLINE_TEXT"
                      subText="MASKED_IDENTIFIER"
                    />
                  </span>
                ))}
              </div>
            </AccountUiVariantSection>
          </CardContent>
        </Card>
      </div>
    </AdminPageContainer>
  );
}
